import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import ImageWithFallback from '@/components/ImageWithFallback';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { Award } from 'lucide-react';
import { seoData } from '@/utils/seoData';

const CertificatesPage = () => {
  const { language } = useLanguage();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    tr: {
      title: 'Sertifikalar ve Ödüller',
      subtitle: 'Kalite ve mükemmelliğimizin kanıtları',
      loading: 'Yükleniyor...',
      issuer: 'Veren Kurum',
      date: 'Tarih',
    },
    en: {
      title: 'Certificates and Awards',
      subtitle: 'Proof of our quality and excellence',
      loading: 'Loading...',
      issuer: 'Issuer',
      date: 'Date',
    },
  };

  const t = content[language];

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        const response = await axios.get(`${API}/certificates`);
        setCertificates(response.data);
      } catch (error) {
        console.error('Error fetching certificates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);

  const pageSeo = seoData.certificates[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="certificates-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="certificates-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Certificates Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center text-gray-600">{t.loading}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {certificates.map((cert) => (
                <div 
                  key={cert.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
                  data-testid={`certificate-card-${cert.id}`}
                >
                  <div className="relative h-64 bg-gray-100">
                    <ImageWithFallback
                      src={cert.image_url} 
                      alt={language === 'tr' ? cert.title_tr : cert.title_en}
                      fallbackType="certificate"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="w-5 h-5 text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {language === 'tr' ? cert.title_tr : cert.title_en}
                      </h3>
                    </div>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>{t.issuer}:</strong> {cert.issuer}</p>
                      <p><strong>{t.date}:</strong> {cert.issue_date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CertificatesPage;